import React, { useEffect, useMemo, useState } from 'react'
import { EColor } from '@frontend/pole-ui/lib/common/constants'
import type { PaginationData } from '@/api/kubik/infoportal'
import {
  GetClientPublicationsQueryParamsType,
  PublicationDataDtoV1Status,
  useGetClientPublications,
  useGetPublicationById,
} from '@/api/kubik/infoportal'
import { RubricsMenu } from '@/components/composed'
import { Articles } from '@/components/resolvers'
import { Breadcrumbs, Lightbox, MetaInfo } from '@/components/ui'
import { homeBreadcrumb } from '@/components/ui/Breadcrumbs/constants'
import { EIntersectionState } from '@/logic/metrika/constants'
import { pushFormattedGtmEvent, pushGtmEvent } from '@/logic/metrika/helpers'
import type { IFormattedGtmEventPayload } from '@/logic/metrika/interfaces'
import { usePushScrollGtmEvent } from '@/logic/metrika/usePushScrollGtmEvent'
import { Redirect404 } from '@/router/components'
import { useLocation } from '@/router/hooks'
import { EJournalRoute } from '@/types'
import { EJournalRubricsLabel } from '@/types/Journal'
import { asyncScrollIntoView } from '@/utils/helpers'
import { useDeviceType } from '@/utils/hooks/useDeviceType'
import { useJournalUrlParams } from '@/utils/infoportal/hooks/useJournalUrlParams'
import { createRouterLink } from '@/utils/url'
import {
  EJournalClickEventBlockId,
  EJournalScrollEventBlockId,
  JournalGtmClickEventPayload,
  JournalGtmScrollEventPayload,
} from '../Journal/gtmConstants'
import { AsideNewsPreview, PublicationHeader, PublicationMainField } from './components'
import { messages } from './messages'
import './styles.scss'

const blockId = EJournalScrollEventBlockId.scrollEndOfArticle

const JournalPublication: React.FC = () => {
  const { isMobile } = useDeviceType()
  const { pathname } = useLocation()
  const { publicationSlug } = useJournalUrlParams()

  const allBlockIds = Object.values(EJournalScrollEventBlockId)
  const [blockIds, setBlockIds] = useState(allBlockIds)

  const { setIntersectedBlocks } = usePushScrollGtmEvent<EJournalScrollEventBlockId, IFormattedGtmEventPayload>({
    blockIds,
    gtmScrollEventPayloads: JournalGtmScrollEventPayload,
  })

  useEffect(() => {
    if (self.scrollY === 0) {
      asyncScrollIntoView('#root').finally(() => {
        setIntersectedBlocks(prev => ({ ...prev, [blockId]: EIntersectionState.didNotIntersect }))
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicationSlug])

  const {
    data: publication,
    isFetching,
    isError: isPublicationError,
  } = useGetPublicationById(publicationSlug ?? '', { query: { staleTime: Infinity, enabled: !!publicationSlug } })

  const publicationRubrics = useMemo(() => publication?.data?.rubrics.filter(rubric => rubric.showInMenu), [publication])

  const currentRubric = publication?.data?.rubrics?.[0]?.id

  const { data: articles, isLoading: isArticlesLoading } = useGetClientPublications(
    { type: GetClientPublicationsQueryParamsType.ARTICLE, rubricId: currentRubric },
    {
      query: {
        staleTime: Infinity,
        select: data => ({
          meta: data.meta,
          data: {
            publications: data.data?.publications.filter(article => article.slug !== publicationSlug).slice(0, 3) || [],
            pagination: {} as PaginationData,
          },
        }),
        enabled: !!currentRubric,
      },
    },
  )

  const { data: newsData, isLoading: isNewsLoading } = useGetClientPublications(
    { type: GetClientPublicationsQueryParamsType.NEWS, includeMain: true },
    {
      query: {
        staleTime: Infinity,
        select: data => ({
          meta: data.meta,
          data: {
            publications: data.data?.publications.filter(article => article.slug !== publicationSlug).slice(0, 4) || [],
            pagination: {} as PaginationData,
          },
        }),
      },
    },
  )

  useEffect(() => {
    if (publication?.data?.type && publication.data.slug === publicationSlug) {
      pushGtmEvent<IFormattedGtmEventPayload>({
        event: 'userEvent',
        pagePart: 'content',
        blockName: null,
        eventCategory: 'interactions',
        eventAction: 'viewing',
        eventLabel: 'pageView',
        eventContext: `pp:${pathname};pt:${publication?.data?.type.toLowerCase()}`,
        eventStatus: 'success',
        eventEcommerce: null,
        eventNonInteraction: '0',
        firingOptions: 'oncePerEvent',
        ecommerce: null,
      })
    }
  }, [pathname, publication, publicationSlug])

  if (isPublicationError || (publication?.data?.status && publication.data.status !== PublicationDataDtoV1Status.PUBLISHED))
    return <Redirect404 />

  const onPreviewClick = (gtmEventPayload: IFormattedGtmEventPayload) => () => {
    setBlockIds([blockId])
    pushFormattedGtmEvent(gtmEventPayload)
  }

  const publicationBreadCrumps = {
    captionId: publicationRubrics?.[0]?.name || EJournalRubricsLabel.MAIN,
    link: publicationRubrics?.[0]?.id
      ? createRouterLink(EJournalRoute.RUBRIC, { rubricId: publicationRubrics[0].id })
      : createRouterLink(EJournalRoute.MAIN),
  }

  const journalBreadcrumbs = [publicationBreadCrumps, homeBreadcrumb]

  return (
    <>
      <MetaInfo
        metaInfo={{
          title: publication?.data?.title,
          description: publication?.data?.subtitle,
          image: publication?.data?.coverImage?.rectangleCover,
        }}
      />
      {isMobile ? (
        <div className="journalArticlesBreadcrumbsMobile">
          <Breadcrumbs
            customBreadcrumbs={journalBreadcrumbs}
            isMobile={isMobile}
            className={'journalArticlesBreadcrumbsMobile__breadcrumb'}
          />
        </div>
      ) : (
        <RubricsMenu />
      )}
      <article className="journalPublication">
        <PublicationHeader publication={publication?.data} />
        <Lightbox>
          <PublicationMainField
            publication={publication?.data}
            isLoading={isFetching}
            AsideNewsPreview={
              (!!newsData?.data?.publications.length || isNewsLoading) && (
                <AsideNewsPreview
                  titleId={messages.asideNewsHeader}
                  news={newsData?.data?.publications || []}
                  mainNewsBackgroundColor={EColor.LIGHT_GRAY}
                  isLoading={isNewsLoading}
                  commonNewsTextId={messages.allNewsCaption}
                  commonNewsUrl={EJournalRoute.NEWS}
                  onClick={onPreviewClick(JournalGtmClickEventPayload.listArticles)}
                />
              )
            }
            blockId={blockId}
          />
        </Lightbox>
      </article>
      <section className="page-wrapper journalPublication__readMore">
        <h2 className="journalPublication__readMoreHeader">{messages.readMoreHeader}</h2>
        {(isArticlesLoading || !!articles?.data?.publications.length) && (
          <Articles
            articles={articles?.data?.publications}
            isArticlesLoading={isArticlesLoading}
            blockId={EJournalClickEventBlockId.listArticles}
            onClick={onPreviewClick(JournalGtmClickEventPayload.listArticles)}
            isInPublication
          />
        )}
      </section>
    </>
  )
}

export default JournalPublication
